<template>
    <pt-popper
        ref="triggerVnode"
        v-model:visible="visible"
        :placement="placement"
        :fallback-placements="['bottom', 'top', 'right', 'left']"
        :effect="effect"
        pure
        :manual-mode="true"
        :trigger="[trigger]"
        popper-class="pt-dropdown__popper"
        append-to-body
        transition="pt-zoom-in-top"
        :stop-popper-mouse-event="false"
        :gpu-acceleration="false"
    >
        <template #default>
            <pt-scrollbar ref="scrollbar" class="pt-dropdown__scrollbar" :style="wrapStyle" content-class="pt-dropdown__list">
                <slot name="dropdown"></slot>
            </pt-scrollbar>
        </template>
        <template #trigger>
            <div :class="['pt-dropdown', dropdownSize ? 'pt-dropdown--' + dropdownSize : '']">
                <slot v-if="!splitButton" name="default"></slot>
                <template v-else>
                    <pt-button-group>
                        <pt-button :size="dropdownSize" :type="type" @click="handlerMainButtonClick">
                            <slot name="default"></slot>
                        </pt-button>
                        <pt-button :size="dropdownSize" :type="type" class="pt-dropdown__caret-button">
                            <pt-icon icon="pt-icon-arrow-down" class="pt-dropdown__icon pt-icon-arrow-down"></pt-icon>
                        </pt-button>
                    </pt-button-group>
                </template>
            </div>
        </template>
    </pt-popper>
</template>
<script lang="ts">
import {
    defineComponent,
    provide,
    getCurrentInstance,
    ref,
    computed,
    watch,
    onMounted,
    ComponentPublicInstance,
} from 'vue';
import { on, addClass, removeClass } from '../../../utils/dom';
import PtButton from '../../button';
// import PtButtonGroup from '../../button-group'
import PtScrollbar from '../../scrollbar';
import PtPopper from '../../popper';
import { useDropdown } from './useDropdown';
import { addUnit } from '../../../utils/util';

export default defineComponent({
    name: 'PtDropdown',
    components: {
        PtButton,
        // PtButtonGroup,
        PtScrollbar,
        PtPopper,
    },
    props: {
        trigger: {
            type: String,
            default: 'hover',
        },
        type: String,
        size: {
            type: String,
            default: '',
        },
        splitButton: Boolean,
        hideOnClick: {
            type: Boolean,
            default: true,
        },
        placement: {
            type: String,
            default: 'bottom',
        },
        showTimeout: {
            type: Number,
            default: 150,
        },
        hideTimeout: {
            type: Number,
            default: 150,
        },
        tabindex: {
            type: [Number, String],
            default: 0,
        },
        effect: {
            type: String,
            default: 'light',
        },
        maxHeight: {
            type: [Number, String],
            default: '',
        },
    },
    emits: ['visible-change', 'click', 'command'],
    setup(props, { emit }) {
        const _instance = getCurrentInstance();
        const { ELEMENT } = useDropdown();

        const timeout = ref<Nullable<number>>(null);

        const visible = ref(false);
        const scrollbar = ref(null);
        const wrapStyle = computed(() => `max-height: ${addUnit(props.maxHeight)}`);

        watch(
            () => visible.value,
            (val) => {
                if (val) triggerElmFocus();
                if (!val) triggerElmBlur();
                emit('visible-change', val);
            },
        );

        const focusing = ref(false);
        watch(
            () => focusing.value,
            (val) => {
                const selfDefine = triggerElm.value;
                if (selfDefine) {
                    if (val) {
                        addClass(selfDefine, 'focusing');
                    } else {
                        removeClass(selfDefine, 'focusing');
                    }
                }
            },
        );

        const triggerVnode = ref<Nullable<ComponentPublicInstance>>(null);
        const triggerElm = computed<Nullable<HTMLButtonElement>>(() => {
            const _: any = (triggerVnode.value?.$refs.triggerRef as HTMLElement)?.children[0] ?? {};
            return !props.splitButton ? _ : _.children?.[1];
        });

        function handleClick() {
            if (triggerElm.value?.disabled) return;
            if (visible.value) {
                hide();
            } else {
                show();
            }
        }

        function show() {
            if (triggerElm.value?.disabled) return;
            timeout.value && clearTimeout(timeout.value);
            timeout.value = window.setTimeout(
                () => {
                    visible.value = true;
                },
                ['click', 'contextmenu'].includes(props.trigger) ? 0 : props.showTimeout,
            );
        }

        function hide() {
            if (triggerElm.value?.disabled) return;
            removeTabindex();
            if (props.tabindex >= 0) {
                resetTabindex(triggerElm.value);
            }
            clearTimeout(timeout.value);
            timeout.value = window.setTimeout(
                () => {
                    visible.value = false;
                },
                ['click', 'contextmenu'].includes(props.trigger) ? 0 : props.hideTimeout,
            );
        }

        function removeTabindex() {
            triggerElm.value?.setAttribute('tabindex', '-1');
        }

        function resetTabindex(ele) {
            removeTabindex();
            ele?.setAttribute('tabindex', '0');
        }

        function triggerElmFocus() {
            triggerElm.value?.focus?.();
        }

        function triggerElmBlur() {
            triggerElm.value?.blur?.();
        }

        const dropdownSize = computed(() => props.size || ELEMENT.size);

        function commandHandler(...args) {
            emit('command', ...args);
        }

        provide('ptDropdown', {
            instance: _instance,
            dropdownSize,
            visible,
            handleClick,
            commandHandler,
            show,
            hide,
            trigger: computed(() => props.trigger),
            hideOnClick: computed(() => props.hideOnClick),
            triggerElm,
        });

        onMounted(() => {
            if (!props.splitButton) {
                on(triggerElm.value, 'focus', () => {
                    focusing.value = true;
                });
                on(triggerElm.value, 'blur', () => {
                    focusing.value = false;
                });
                on(triggerElm.value, 'click', () => {
                    focusing.value = false;
                });
            }
            if (props.trigger === 'hover') {
                on(triggerElm.value, 'mouseenter', show);
                on(triggerElm.value, 'mouseleave', hide);
            } else if (props.trigger === 'click') {
                on(triggerElm.value, 'click', handleClick);
            } else if (props.trigger === 'contextmenu') {
                on(triggerElm.value, 'contextmenu', (e) => {
                    e.preventDefault();
                    handleClick();
                });
            }

            Object.assign(_instance, {
                handleClick,
                hide,
                resetTabindex,
            });
        });

        const handlerMainButtonClick = (event) => {
            emit('click', event);
            hide();
        };

        return {
            visible,
            scrollbar,
            wrapStyle,
            dropdownSize,
            handlerMainButtonClick,
            triggerVnode,
            triggerElm,
        };
    },
});
</script>

<style lang="scss" src="../style/dropdown.scss"></style>
